import React, {
  createContext,
  MutableRefObject,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import reverse from 'lodash/reverse';
import { ResizeObserver, ResizeObserverEntry } from '@juggle/resize-observer';
import { isClient } from '../../util/helpers';
import { navItemsHeight } from './constants';

export type Refs = readonly [
  MutableRefObject<HTMLDivElement | null>,
  MutableRefObject<HTMLDivElement | null>,
  MutableRefObject<HTMLDivElement | null>,
  MutableRefObject<HTMLDivElement | null>,
];

//
const pixelBuffer = 6;

export type HandleSelectSection = (ref: MutableRefObject<HTMLDivElement | null>) => void;

type PuppyGuideContextType = {
  refSections: Refs;
  stickyHeaderTopOffset: number;
  handleSelectSection: HandleSelectSection;
  selectedSection: MutableRefObject<HTMLDivElement | null>;
  headerStickyStartLocationRef: MutableRefObject<HTMLDivElement | null>;
  headerStickyEndLocationRef: MutableRefObject<HTMLDivElement | null>;
  isHeaderSticky: boolean;
};

export const PuppyGuideContext = createContext<PuppyGuideContextType | null>(null);

export const calculateStickyHeaderTopOffset = (dimensions: DOMRect) => {
  const isElementVisible = dimensions.top + dimensions.height >= 0;
  return !isElementVisible ? 0 : dimensions.height;
};

const PuppyGuideProvider = ({ children }: { children: React.ReactNode }) => {
  const headerStickyStartLocationRef = useRef<HTMLDivElement>(null);
  const headerStickyEndLocationRef = useRef<HTMLDivElement>(null);
  const sectionOne = useRef<HTMLDivElement>(null);
  const sectionTwo = useRef<HTMLDivElement>(null);
  const sectionThree = useRef<HTMLDivElement>(null);
  const sectionFour = useRef<HTMLDivElement>(null);
  const [selectedSection, setSelectedSection] = useState<MutableRefObject<HTMLDivElement | null>>(
    sectionOne,
  );
  const handleSelectSection = (ref: MutableRefObject<HTMLDivElement | null>) => {
    let scrollToLocation = (ref?.current?.getBoundingClientRect()?.top || 0) + window.scrollY;
    if (!isHeaderSticky) {
      scrollToLocation = scrollToLocation + navItemsHeight;
    }
    window.scrollTo({ behavior: 'smooth', top: scrollToLocation });
  };
  const [stickyHeaderTopOffset, setStickyHeaderTopOffset] = useState(0);
  const mainHeader = useRef(
    isClient() ? document.querySelector('[data-testid=sticky-header]') : null,
  );
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  const determineStickyHeaderTopOffset = (header: Element) => {
    const styles = window.getComputedStyle(header);
    let calculatedStickyHeaderTopOffset: number;
    if (styles.visibility === 'hidden' || styles.display === 'none') {
      calculatedStickyHeaderTopOffset = 0;
    } else {
      const dimensions = header.getBoundingClientRect();
      calculatedStickyHeaderTopOffset = calculateStickyHeaderTopOffset(dimensions);
    }

    if (stickyHeaderTopOffset !== calculatedStickyHeaderTopOffset) {
      setStickyHeaderTopOffset(calculatedStickyHeaderTopOffset);
    }
  };

  useLayoutEffect(() => {
    // header does not appear immediately, so need to poll for it.
    let timer: number | undefined;
    const observeHeaderCallback = (
      entries: IntersectionObserverEntry[] | ResizeObserverEntry[],
    ) => {
      const header = entries[0].target;
      determineStickyHeaderTopOffset(header);
    };
    const ro = new ResizeObserver(observeHeaderCallback);
    if (mainHeader.current) {
      ro.observe(mainHeader.current);
    }
    return () => {
      ro.disconnect();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [mainHeader, stickyHeaderTopOffset, setStickyHeaderTopOffset]);

  useLayoutEffect(() => {
    const toggleSticky = (
      { top: StartTop }: { top: number },
      { top: StopTop }: { top: number },
    ) => {
      if (StartTop <= 0) {
        if (!isHeaderSticky) {
          mainHeader.current && determineStickyHeaderTopOffset(mainHeader.current);
          setIsHeaderSticky(true);
        }
      } else if (StopTop >= pixelBuffer) {
        isHeaderSticky && setIsHeaderSticky(false);
      }
    };
    const handleScroll = () => {
      const firstRefRect = refSections[0].current?.getBoundingClientRect();
      if (headerStickyStartLocationRef?.current && firstRefRect) {
        toggleSticky(headerStickyStartLocationRef.current.getBoundingClientRect(), firstRefRect);
      }
      const selectedRef = reverse([...refSections]).find((ref) => {
        const top = ref?.current?.getBoundingClientRect()?.top || pixelBuffer;
        return top < pixelBuffer;
      });
      if (selectedRef && selectedRef !== selectedSection) {
        setSelectedSection(selectedRef);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHeaderSticky, setIsHeaderSticky, selectedSection, setSelectedSection]);

  const refSections: Refs = [sectionOne, sectionTwo, sectionThree, sectionFour];
  const value = {
    refSections,
    stickyHeaderTopOffset,
    handleSelectSection,
    selectedSection,
    headerStickyEndLocationRef,
    headerStickyStartLocationRef,
    isHeaderSticky,
  };
  return <PuppyGuideContext.Provider value={value}>{children}</PuppyGuideContext.Provider>;
};

const usePuppyGuide = () => {
  const context = useContext(PuppyGuideContext);

  if (context === null) {
    throw new Error('usePuppyGuide must be used within a PuppyGuideContext');
  }

  return context;
};

export { PuppyGuideProvider, usePuppyGuide };
